import { ChangeDetectionStrategy, Component, computed, inject, signal, viewChild } from '@angular/core';

import { AuthStateService } from '@arya/auth/data-access';
import { RegulatoryProfileData } from '@arya/currency/data-access';
import {
  DEV_ADMIN_EMAIL,
  DEV_FRESH_INVESTOR_EMAIL,
  DEV_INVESTOR_EMAIL,
  DEV_ONBOARDING_INVESTOR_EMAIL,
} from '@arya/shared/dev-accounts';
import { PLATFORM_BASE_PATH } from '@arya/shared/feature';

import { NoxoButton, NoxoIconButton } from '@noxo/ui/button';
import { NoxoCardOverline } from '@noxo/ui/card';
import { NoxoDialog, NoxoDialogHeader } from '@noxo/ui/dialog';
import { NoxoIcon } from '@noxo/ui/icon';
import { NoxoListRow, NoxoListRowTitle } from '@noxo/ui/list-row';
import { type MessageType, NoxoMessage } from '@noxo/ui/message';

import { DemoInstallations } from './demo-installations';
import { type DemoJourney, type DemoJourneyPersona, demoJourneys, demoScreens } from './demo-journey';
import { DemoJourneyList } from './demo-journey-list';
import type { DemoPersona } from './demo-persona';
import { DemoPersonaList } from './demo-persona-list';
import { demoPersonas } from './demo-personas';
import { DemoReset } from './demo-reset';
import { DemoSignIn } from './demo-sign-in';
import { DemoTools } from './demo-tools';

const JOURNEY_PERSONA_EMAIL: Readonly<Record<Exclude<DemoJourneyPersona, 'guest'>, string>> = {
  returning: DEV_INVESTOR_EMAIL,
  new: DEV_FRESH_INVESTOR_EMAIL,
  inReview: DEV_ONBOARDING_INVESTOR_EMAIL,
};

@Component({
  selector: 'noxo-demo-overlay',
  imports: [
    DemoInstallations,
    DemoJourneyList,
    DemoPersonaList,
    DemoTools,
    NoxoButton,
    NoxoCardOverline,
    NoxoDialog,
    NoxoDialogHeader,
    NoxoIcon,
    NoxoIconButton,
    NoxoListRow,
    NoxoListRowTitle,
    NoxoMessage,
  ],
  providers: [DemoSignIn, DemoReset],
  templateUrl: './noxo-demo-overlay.html',
  styleUrls: ['./noxo-demo-overlay.scss', './demo-heading.scss', './demo-fab.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class NoxoDemoOverlay {
  private readonly signIn = inject(DemoSignIn);
  private readonly demoReset = inject(DemoReset);
  private readonly basePath = inject(PLATFORM_BASE_PATH);
  private readonly currentUser = inject(AuthStateService).currentUser;
  private readonly dialog = viewChild.required(NoxoDialog);

  private readonly regime = inject(RegulatoryProfileData).regime;

  protected readonly personas = computed(() => demoPersonas(this.basePath, this.regime() ?? 'ecspr'));
  protected readonly admin = this.persona(DEV_ADMIN_EMAIL);
  protected readonly journeys = demoJourneys(this.basePath);
  protected readonly screens = demoScreens(this.basePath);
  protected readonly busy = signal(false);
  protected readonly notice = signal<{ readonly type: MessageType; readonly text: string } | undefined>(undefined);
  protected readonly signedInAs = computed(() => this.currentUser()?.email ?? '');

  protected open(): void {
    this.notice.set(undefined);
    this.dialog().showModal();
  }

  protected close(): void {
    this.dialog().close();
  }

  protected async pick(persona: DemoPersona, target?: string): Promise<void> {
    await this.run('Sign-in failed.', async () => {
      await this.signIn.signInAs(persona, target);
      this.close();
    });
  }

  protected async go(journey: DemoJourney): Promise<void> {
    if (journey.persona !== 'guest') {
      await this.pick(this.persona(JOURNEY_PERSONA_EMAIL[journey.persona]), journey.path);
      return;
    }
    await this.run('Sign-out failed.', async () => {
      await this.signIn.signOutTo(journey.path);
      this.close();
    });
  }

  protected async reset(): Promise<void> {
    await this.run('Demo reset failed.', async () => {
      await this.demoReset.reset();
      this.notice.set({ type: 'success', text: 'Demo personas are back to their seeded state.' });
    });
  }

  private persona(email: string): DemoPersona {
    const found = this.personas().find((persona) => persona.email === email);
    if (found === undefined) throw new Error(`No demo persona ${email}`);
    return found;
  }

  private async run(failure: string, action: () => Promise<void>): Promise<void> {
    if (this.busy()) return;
    this.busy.set(true);
    this.notice.set(undefined);
    try {
      await action();
    } catch (cause: unknown) {
      this.notice.set({ type: 'error', text: cause instanceof Error ? cause.message : failure });
    } finally {
      this.busy.set(false);
    }
  }
}
